import { useEffect, useState } from 'react';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';

import SoftButton from 'components/SoftButton';
import SoftTypography from 'components/SoftTypography';

import type { PlatformDictionary } from 'lib/i18n/types';
import { interpolateTemplate } from 'lib/i18n/dictionary';
import {
  getHiddenReasonKey,
  REVIEW_HIDDEN_REASONS,
  type ModeratedReview,
  type ReviewHiddenReason,
} from 'review';

interface HideReviewDialogProps {
  review: ModeratedReview | null;
  isSubmitting: boolean;
  translations: PlatformDictionary['reviewsPage'];
  onCancel: () => void;
  onConfirm: (reason: ReviewHiddenReason) => void;
}

export function HideReviewDialog({
  review,
  isSubmitting,
  translations,
  onCancel,
  onConfirm,
}: HideReviewDialogProps) {
  const [reason, setReason] = useState<ReviewHiddenReason | null>(null);

  useEffect(() => {
    setReason(null);
  }, [review?.uuid]);

  return (
    <Dialog
      open={Boolean(review)}
      onClose={isSubmitting ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      aria-labelledby="hide-review-title"
      data-testid="hide-review-dialog"
    >
      <DialogTitle id="hide-review-title">
        {interpolateTemplate(translations.hideDialogTitle, { author: review?.authorName ?? '' })}
      </DialogTitle>
      <DialogContent>
        <SoftTypography variant="button" color="text" fontWeight="regular" component="p" mb={2}>
          {translations.hideDialogDescription}
        </SoftTypography>
        <FormControl component="fieldset">
          <FormLabel component="legend">
            <SoftTypography variant="caption" fontWeight="bold">
              {translations.reasonLabel}
            </SoftTypography>
          </FormLabel>
          <RadioGroup
            name="review-hidden-reason"
            value={reason ?? ''}
            onChange={(event) => setReason(event.target.value as ReviewHiddenReason)}
          >
            {REVIEW_HIDDEN_REASONS.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio />}
                label={translations[getHiddenReasonKey(option)]}
                disabled={isSubmitting}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <SoftButton
          type="button"
          variant="outlined"
          color="secondary"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {translations.cancel}
        </SoftButton>
        <SoftButton
          type="button"
          variant="gradient"
          color="dark"
          onClick={() => {
            if (reason) {
              onConfirm(reason);
            }
          }}
          disabled={!reason || isSubmitting}
          data-testid="hide-review-confirm"
        >
          {translations.hideAction}
        </SoftButton>
      </DialogActions>
    </Dialog>
  );
}
